/* SIZESOFTX
=========================================================================================
* Descripción: Modal de préstamos a empleados
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 14/08/2026 | Lic. José Luis Aragón Cervantes  | Creación de registro de préstamos a empleados
*      2       | 14/08/2026 | Lic. José Luis Aragón Cervantes  | Integra búsqueda y lector de artículos en préstamo en especie
*      3       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Apertura automática de cajón para préstamo en efectivo
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, FormControlLabel, Grid, Stack, Typography } from "@mui/material";
import SizeSoftXModal from "../leyout/SizeSoftXModal";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useSesion } from "../../context/System/SesionContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import {
    apiBuscarArticuloPorCodigoBarras,
    apiBuscararticulosFiltroFull,
    apiCargarArticuloPorClave,
    apiMovimientoPrestamo,
    apiNominasUsuarios,
    apiPrestamosRegistrar,
    apiValidarStock,
} from "../../settings/ApiConfig";

const formatCurrency = (value) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(value || 0));

const initialForm = {
    empleado: null,
    concepto: "",
    monto: "",
    observaciones: "",
    enEspecie: false,
    articulo: null,
    clavearticulo: "",
    descripcionarticulo: "",
    medida: "",
    cantidad: "1",
    preciounitario: "",
    factor: 1,
    codigoBarras: "",
};

const getClaveArticulo = (articulo) => String(articulo?.clavearticulo || articulo?.sClaveArticulo || articulo?.articulo?.clavearticulo || articulo?.articulo?.sClaveArticulo || "").trim();
const getDescripcionArticulo = (articulo) => String(articulo?.descripcion || articulo?.sDescripcion || articulo?.descripcionarticulo || articulo?.articulo?.descripcion || articulo?.articulo?.sDescripcion || "").trim();
const getMedidaArticulo = (articulo) => Number(articulo?.medida ?? articulo?.fMedida ?? articulo?.dMedida ?? 0);
const getPrecioArticulo = (articulo) => Number(articulo?.preciodefinitivo ?? articulo?.dPrecioDefinitivo ?? articulo?.precioDefinitivo ?? articulo?.preciounitario ?? 0);
const getMedidasArticulo = (data) => data?.medidas || data?.dtoMedidas || data?.listamedidas || data?.listaMedidas || [];
const normalizarMedida = (medida) => ({
    ...medida,
    medida: Number(medida?.medida ?? medida?.fMedida ?? medida?.dMedida ?? 0),
    preciodefinitivo: Number(medida?.preciodefinitivo ?? medida?.dPrecioDefinitivo ?? 0),
    codigobarras: String(medida?.codigobarras || medida?.sCodigoBarras || "").trim(),
});

export default function ModalPrestamoEmpleado({ open, onClose, onSaved, usuarioAutoriza = "", caja = "" }) {
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleAbrirCajonDinero } = useVentas();
    const [empleados, setEmpleados] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [guardando, setGuardando] = useState(false);
    const [articulos, setArticulos] = useState([]);
    const [buscandoArticulos, setBuscandoArticulos] = useState(false);
    const [medidas, setMedidas] = useState([]);
    const searchTimerRef = useRef(null);
    const codigoRef = useRef(null);

    const medidaSeleccionada = useMemo(() => (
        medidas.find((item) => Number(item.medida) === Number(form.medida || 0)) || null
    ), [medidas, form.medida]);

    const montoCalculado = useMemo(() => {
        if (!form.enEspecie) return Number(form.monto || 0);
        return Number(form.cantidad || 0) * Number(form.preciounitario || 0);
    }, [form]);

    useEffect(() => {
        if (!open) return;
        setForm(initialForm);
        setArticulos([]);
        setMedidas([]);
        const cargarEmpleados = async () => {
            const response = await GetRequest({ url: apiNominasUsuarios });
            if (!response.hasError) setEmpleados(response.result || []);
        };
        cargarEmpleados();
    }, [open]);

    useEffect(() => () => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    }, []);

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const aplicarArticulo = async (articulo, medidaPreferida = null) => {
        const clave = getClaveArticulo(articulo);
        if (!clave) {
            handleOpenAlert("No fue posible obtener la clave del artículo", "warning");
            return;
        }

        const response = await GetRequest({
            url: apiCargarArticuloPorClave.replace("{clavearticulo}", encodeURIComponent(clave)),
            loader: "Cargando medidas del artículo...",
        });

        if (response.hasError) {
            handleOpenAlert(response.message || "No fue posible cargar el artículo", "error");
            return;
        }

        const full = response.result || {};
        const articuloBase = full.articulo || articulo;
        const medidasArticulo = getMedidasArticulo(full).map(normalizarMedida).filter((item) => item.medida > 0);
        const descripcion = getDescripcionArticulo(articuloBase) || getDescripcionArticulo(articulo);
        const factorArticulo = Number(articuloBase?.factor ?? articuloBase?.fFactor ?? articulo?.factor ?? 0) || 1;
        const medidaInicial = Number(medidaPreferida || getMedidaArticulo(articulo) || medidasArticulo[0]?.medida || 0);
        const medidaBase = medidasArticulo.find((item) => Number(item.medida) === medidaInicial) || medidasArticulo[0] || null;
        const precio = medidaBase?.preciodefinitivo || getPrecioArticulo(articulo) || "";
        const almacenexhibidor = (parametrosCajaForm?.dominio || "ALMACEN1").trim();
        const medidaValidar = medidaBase?.medida || medidaInicial;

        try {
            const stockResponse = await GetRequest({
                url: apiValidarStock
                    .replace("{clavearticulo}", encodeURIComponent(clave))
                    .replace("{medida}", medidaValidar)
                    .replace("{almacen}", encodeURIComponent(almacenexhibidor)),
                loader: "Validando existencia del artículo...",
            });
            const existencia = Number(stockResponse?.result ?? 0);
            if (existencia <= 0) {
                handleOpenAlert(`El artículo no tiene existencia en el almacén ${almacenexhibidor}`, "warning");
                setField("codigoBarras", "");
                codigoRef.current?.focus();
                return;
            }
        } catch (err) {
            handleOpenAlert("No fue posible validar la existencia del artículo", "error");
            return;
        }

        setMedidas(medidasArticulo);
        setForm((prev) => ({
            ...prev,
            enEspecie: true,
            articulo: { ...articuloBase, clavearticulo: clave, descripcion },
            clavearticulo: clave,
            descripcionarticulo: descripcion,
            medida: medidaBase?.medida || medidaInicial || "",
            preciounitario: precio,
            factor: factorArticulo,
            concepto: prev.concepto || `Préstamo en especie: ${descripcion || clave}`,
        }));
    };

    const buscarArticuloPorCodigo = async () => {
        const codigo = String(form.codigoBarras || "").trim();
        if (!codigo) return;

        const response = await GetRequest({
            url: apiBuscarArticuloPorCodigoBarras.replace("{codigobarras}", encodeURIComponent(codigo)),
            loader: "Buscando artículo por código de barras...",
        });

        if (response.hasError || !response.result) {
            handleOpenAlert(response.message || "No se encontró artículo para el código capturado", "warning");
            setField("codigoBarras", "");
            codigoRef.current?.focus();
            return;
        }

        await aplicarArticulo(response.result, getMedidaArticulo(response.result));
        setField("codigoBarras", "");
        codigoRef.current?.focus();
    };

    const buscarArticulosDescripcion = (texto) => {
        const filtro = String(texto || "").trim();
        setField("descripcionarticulo", texto || "");
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (filtro.length < 2) {
            setArticulos([]);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            setBuscandoArticulos(true);
            try {
                const url = apiBuscararticulosFiltroFull
                    .replace("{busqueda}", encodeURIComponent(filtro))
                    .replace("{registroinicial}", 0)
                    .replace("{totalregistros}", 30);
                const response = await GetRequest({ url });
                if (response.hasError) {
                    setArticulos([]);
                    return;
                }
                setArticulos(response.result?.listaarticulos || []);
            } finally {
                setBuscandoArticulos(false);
            }
        }, 250);
    };

    const seleccionarMedida = (value) => {
        const medida = value ? normalizarMedida(value) : null;
        setForm((prev) => ({
            ...prev,
            medida: medida?.medida || "",
            preciounitario: medida?.preciodefinitivo || prev.preciounitario || "",
        }));
    };

    const getTicketLineas = (prestamo) => {
        const encabezado = (parametrosCajaForm?.encabezadoticket || "SIZESOFTX").trim();
        const pie = (parametrosCajaForm?.pieticket || "").trim();
        const lineas = [
            encabezado,
            "--------------------------------",
            "RECIBO DE PRESTAMO",
            `Folio: ${prestamo.folioprestamo || ""}`,
            `Fecha: ${new Date().toLocaleString("es-MX")}`,
            `Empleado: ${prestamo.nombreempleado || form.empleado?.nombre || ""}`,
            `Usuario: ${prestamo.usuarioempleado || form.empleado?.usuario || ""}`,
            `Caja: ${prestamo.caja || caja || parametrosCajaForm?.caja || ""}`,
            "--------------------------------",
            `Concepto: ${prestamo.concepto || form.concepto}`,
            `Tipo: ${prestamo.tipoprestamo || (form.enEspecie ? "MERCANCIA" : "EFECTIVO")}`,
        ];
        if (form.enEspecie) {
            lineas.push(`Articulo: ${form.clavearticulo}`);
            lineas.push(`${form.descripcionarticulo}`);
            lineas.push(`Medida: ${form.medida} Cant: ${form.cantidad}`);
        }
        lineas.push(`Monto: ${formatCurrency(prestamo.montoprestamo || montoCalculado)}`);
        lineas.push("--------------------------------");
        lineas.push("Recibí de conformidad");
        lineas.push("Firma: " + (usuarioAutoriza || usuarioData?.Usuario || ""));
        lineas.push("Autoriza: " + (usuarioAutoriza || usuarioData?.Usuario || ""));
        if (pie) lineas.push(pie);
        return lineas;
    };

    const imprimirTicket = async (prestamo) => {
        const printerName = parametrosCajaForm?.nombreimpresoraapartados?.trim() || parametrosCajaForm?.nombreimpresoraventas?.trim() || localStorage.getItem("nombreimpresoraventas") || "";
        if (!printerName) return;
        if (!form.enEspecie) await handleAbrirCajonDinero(printerName);
        const textEncoder = new TextEncoder();
        const bytes = textEncoder.encode("\x1B\x40" + getTicketLineas(prestamo).join("\n") + "\n\n\n\n\x1D\x56\x00");
        const formData = new FormData();
        formData.append("ticket", new Blob([bytes], { type: "application/octet-stream" }));
        formData.append("printerName", printerName);
        await fetch("http://localhost:4000/api/imprimirticket", { method: "POST", body: formData });
    };

    const guardar = async () => {
        if (!form.empleado?.id && !form.empleado?.usuario) { handleOpenAlert("Seleccione un empleado", "warning"); return; }
        if (!form.concepto.trim()) { handleOpenAlert("Capture el concepto del préstamo", "warning"); return; }
        if (montoCalculado <= 0) { handleOpenAlert("El monto del préstamo debe ser mayor a cero", "warning"); return; }
        if (form.enEspecie && !form.clavearticulo.trim()) { handleOpenAlert("Capture el artículo entregado en especie", "warning"); return; }
        if (form.enEspecie && Number(form.medida || 0) <= 0) { handleOpenAlert("Seleccione la medida del artículo", "warning"); return; }

        setGuardando(true);
        try {
            const pc = (caja || parametrosCajaForm?.caja || "").trim().toUpperCase();
            const almacenexhibidor = (parametrosCajaForm?.dominio || "ALMACEN1").trim();

            const body = {
                idaccesoempleado: Number(form.empleado.id || form.empleado.idaccesoempleado || 0),
                usuarioempleado: form.empleado.usuario || "",
                nombreempleado: form.empleado.nombre || "",
                concepto: form.concepto,
                tipoprestamo: form.enEspecie ? "MERCANCIA" : "EFECTIVO",
                montoprestamo: Number(montoCalculado.toFixed(2)),
                clavearticulo: form.enEspecie ? form.clavearticulo : "",
                descripcionarticulo: form.enEspecie ? form.descripcionarticulo : "",
                medida: Number(form.medida || 0),
                cantidad: form.enEspecie ? Number(form.cantidad || 0) : 0,
                preciounitario: form.enEspecie ? Number(form.preciounitario || 0) : 0,
                almacenexhibidor: form.enEspecie ? almacenexhibidor : "",
                foliosalidainventario: "",
                observaciones: form.observaciones,
                usuarioautoriza: usuarioAutoriza || usuarioData?.Usuario || "",
                usuarioregistro: usuarioData?.Usuario || "",
                caja: caja || parametrosCajaForm?.caja || "",
            };
            const response = await PostRequest({ url: apiPrestamosRegistrar, body, loader: "Registrando préstamo..." });
            if (response.hasError) { handleOpenAlert(response.message, "error"); return; }

            const prestamoRegistrado = response.result || body;
            const folioPrestamo = String(prestamoRegistrado.folioprestamo || prestamoRegistrado.FolioPrestamo || "").trim();

            if (form.enEspecie) {
                const movimientoBody = {
                    folio: folioPrestamo,
                    clavearticulo: form.clavearticulo,
                    medida: Number(form.medida || 0),
                    cantidad: Number(form.cantidad || 0),
                    factor: Number(form.factor || 1),
                    concepto: form.concepto,
                    almacenexhibidor,
                    usuario: usuarioData?.Usuario || "",
                    pc,
                };
                const movimientoResponse = await PostRequest({
                    url: apiMovimientoPrestamo,
                    body: movimientoBody,
                    loader: "Registrando salida de inventario del préstamo...",
                });
                if (movimientoResponse?.hasError) {
                    handleOpenAlert("Préstamo registrado, pero error en la salida de inventario: " + (movimientoResponse?.message || ""), "error");
                    return;
                }
            }

            await imprimirTicket(prestamoRegistrado).catch(() => {});
            handleOpenAlert("Préstamo registrado correctamente", "success");
            onSaved?.(prestamoRegistrado);
            onClose?.();
        } catch (error) {
            handleOpenAlert("Error al registrar préstamo: " + error.message, "error");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <SizeSoftXModal open={open} title="Préstamo a empleado" maxWidth="md" success="Aceptar" onSuccess={guardar} onClose={onClose} disableSuccess={guardando}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Autocomplete
                    options={empleados}
                    value={form.empleado}
                    onChange={(_, value) => setField("empleado", value)}
                    getOptionLabel={(option) => `${option?.nombre || ""} (${option?.usuario || ""})`}
                    renderInput={(params) => <TextField autoComplete="off" {...params} label="Empleado" size="small" />}
                />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <TextField autoComplete="off" fullWidth size="small" label="Concepto" value={form.concepto} onChange={(e) => setField("concepto", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField autoComplete="off" fullWidth size="small" label="Monto préstamo" type="text" inputMode="numeric" enterKeyHint="enter" value={form.monto} disabled={form.enEspecie} onChange={(e) => setField("monto", e.target.value)} />
                    </Grid>
                </Grid>
                <FormControlLabel control={<Checkbox checked={form.enEspecie} onChange={(e) => setField("enEspecie", e.target.checked)} />} label="Préstamo en especie" />
                {form.enEspecie && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Lector código de barras"
                                value={form.codigoBarras}
                                inputRef={codigoRef}
                                autoComplete="off"
                                onChange={(e) => setField("codigoBarras", e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        buscarArticuloPorCodigo();
                                    }
                                    if (e.key === "Escape" || e.key === "Delete") setField("codigoBarras", "");
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Autocomplete
                                options={articulos}
                                value={form.articulo}
                                loading={buscandoArticulos}
                                inputValue={form.descripcionarticulo}
                                onInputChange={(_, value, reason) => {
                                    if (reason !== "reset") buscarArticulosDescripcion(value);
                                }}
                                onChange={(_, value) => value && aplicarArticulo(value)}
                                getOptionLabel={(option) => `${getDescripcionArticulo(option)} - ${getClaveArticulo(option)}`.trim()}
                                isOptionEqualToValue={(option, value) => getClaveArticulo(option) === getClaveArticulo(value)}
                                renderInput={(params) => <TextField autoComplete="off" {...params} label="Descripción / clave artículo" size="small" />}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}><TextField autoComplete="off" fullWidth size="small" label="Clave artículo" value={form.clavearticulo} onChange={(e) => setField("clavearticulo", e.target.value)} /></Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                options={medidas}
                                value={medidaSeleccionada}
                                onChange={(_, value) => seleccionarMedida(value)}
                                getOptionLabel={(option) => option?.medida ? String(option.medida) : ""}
                                isOptionEqualToValue={(option, value) => Number(option.medida) === Number(value.medida)}
                                renderInput={(params) => <TextField autoComplete="off" {...params} label="Medida" size="small" />}
                            />
                        </Grid>
                        <Grid item xs={6} md={2}><TextField autoComplete="off" fullWidth size="small" label="Cantidad" type="text" inputMode="numeric" enterKeyHint="enter" value={form.cantidad} onChange={(e) => setField("cantidad", e.target.value)} /></Grid>
                        <Grid item xs={6} md={2}><TextField autoComplete="off" fullWidth size="small" label="Precio unitario" type="text" inputMode="numeric" enterKeyHint="enter" value={form.preciounitario} onChange={(e) => setField("preciounitario", e.target.value)} /></Grid>
                    </Grid>
                )}
                <TextField autoComplete="off" fullWidth size="small" multiline minRows={2} label="Observaciones" value={form.observaciones} onChange={(e) => setField("observaciones", e.target.value)} />
                <Box sx={{ textAlign: "right" }}><Typography fontWeight={700}>Total préstamo: {formatCurrency(montoCalculado)}</Typography></Box>
            </Stack>
        </SizeSoftXModal>
    );
}
